import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppEquipmentTypeSelectField, AppInputField } from '@/form/FormUI';
import type { NodeFormProps, SubGuideNodeType } from '@/nodes';
import { equipmentTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubGuideDataSchema, type SubGuideDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof SubGuideDataSchema>;

const SubGuideForm: FC<NodeFormProps<SubGuideNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, SubGuideDataType>({
        resolver: zodResolver(SubGuideDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const selectedEquipmentTypeId = form.watch('equipmentTypeId');
    useEffect(() => {
        if (!selectedEquipmentTypeId) return;
        const equipmentType = equipmentTypes?.find((t) => t.id === selectedEquipmentTypeId);
        if (equipmentType) {
            const kebab = equipmentType.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            form.setValue('scopeKey', `$${kebab}`);
        }
    }, [selectedEquipmentTypeId, equipmentTypes, form]);

    const onSubmit = useCallback(
        (values: SubGuideDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppEquipmentTypeSelectField
                        control={form.control}
                        name="equipmentTypeId"
                        label="Equipment Type"
                        description="Which equipment guide to inline as a sub-guide."
                        equipmentTypes={equipmentTypes}
                    />

                    <AppInputField
                        control={form.control}
                        name="scopeKey"
                        label="Scope Key"
                        placeholder="$thermostat"
                        description="Value namespace for sub-guide values (e.g., $thermostat)."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default SubGuideForm;
